import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Link, usePage, router } from '@inertiajs/react'
import {
  Calendar,
  Home,
  Users,
  Briefcase,
  CreditCard,
  Settings as SettingsIcon,
  Receipt,
  Bell,
  Plus,
  Menu,
  ArrowLeft,
  ChevronDown,
  Megaphone,
  MessageSquare,
} from 'lucide-react'
import { toast } from 'sonner'
import { Toaster } from '@/components/ui/sonner'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { SharedProps } from '@/types'
import Logo from '@/components/Logo'
import NewCampaignDialog from '@/components/NewCampaignDialog'

type NavItem = {
  href: string
  label: string
  icon: typeof Calendar
  exact?: boolean
}

type NavGroup = {
  label: string
  items: NavItem[]
}

function buildNavGroups(isPro: boolean, isOwner: boolean): NavGroup[] {
  const accountItems: NavItem[] = [
    { href: '/dashboard/settings/notifications', label: 'Notifications', icon: Bell },
    { href: '/dashboard/settings/team', label: 'Team', icon: Users },
    { href: '/dashboard/settings', label: 'Settings', icon: SettingsIcon, exact: true },
  ]
  if (isOwner) {
    accountItems.unshift({ href: '/dashboard/settings/payments', label: 'Payments', icon: CreditCard })
    accountItems.push({
      href: '/dashboard/billing',
      label: isPro ? 'Billing' : 'Upgrade to Pro',
      icon: Receipt,
    })
  }
  return [
    {
      label: 'Primary',
      items: [
        { href: '/dashboard', label: 'Home', icon: Home, exact: true },
        { href: '/dashboard/events', label: 'Campaigns', icon: Megaphone },
        { href: '/dashboard/posts', label: 'Posts', icon: MessageSquare },
        { href: '/dashboard/guests', label: 'Transactions', icon: Users },
      ],
    },
    {
      label: 'Resources',
      items: [
        { href: '/dashboard/saved_services', label: 'Saved Services', icon: Briefcase },
      ],
    },
    {
      label: 'Account',
      items: accountItems,
    },
  ]
}

function isActive(currentUrl: string, href: string, exact?: boolean) {
  if (exact) return currentUrl === href || currentUrl.startsWith(`${href}?`)
  return currentUrl === href || currentUrl.startsWith(`${href}/`) || currentUrl.startsWith(`${href}?`)
}

function SidebarBody({ onNavigate }: { onNavigate?: () => void }) {
  const { current_organization: org, current_member: member } = usePage<SharedProps>().props
  const url = usePage().url
  if (!org) return null

  const isOwner = member?.role === 'owner'
  const groups = buildNavGroups(org.pro, isOwner)
  const initial = org.name?.[0]?.toUpperCase() ?? '?'
  const memberName = member ? `${member.first_name} ${member.last_name}`.trim() : null

  return (
    <div className="flex h-full flex-col">
      <div className="px-4 py-4 border-b border-black/5" onClick={onNavigate}>
        <Logo className="text-xl" href="/dashboard" ariaLabel="Dashboard home" />
      </div>

      <div className="px-3 py-3 border-b border-black/5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-auto w-full justify-start gap-2 px-2 py-2 text-left"
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback>{initial}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-semibold">{org.name}</p>
                  {org.pro && (
                    <Badge variant="default" className="px-1.5 py-0 text-[10px]">Pro</Badge>
                  )}
                </div>
                <p className="truncate text-xs font-normal text-muted-foreground">
                  {memberName ? `${memberName} · ${member?.role === 'owner' ? 'Owner' : 'Member'}` : org.email}
                </p>
              </div>
              <ChevronDown className="size-4 shrink-0 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel className="truncate">{org.name}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link href={`/o/${org.slug}`} onClick={onNavigate}>Public profile</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => router.delete('/organizations/sign_out')}>
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <NewCampaignDialog
          onNavigate={onNavigate}
          trigger={(
            <Button className="mt-2 w-full">
            <Plus className="size-4" />
            New Campaign
            </Button>
          )}
        />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
        {groups.map(group => (
          <div key={group.label}>
            <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {group.label}
            </p>
            <ul className="space-y-0.5">
              {group.items.map(item => {
                const active = isActive(url, item.href, item.exact)
                const Icon = item.icon
                return (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      onClick={onNavigate}
                      className={`flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition ${
                        active
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-foreground/80 hover:bg-black/5 hover:text-foreground'
                      }`}
                    >
                      <Icon className="size-4 shrink-0" />
                      {item.label}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-black/5 px-4 py-3">
        <Link
          href="/"
          onClick={onNavigate}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground transition hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to site
        </Link>
      </div>
    </div>
  )
}

export default function DashboardLayout({ children }: { children: ReactNode }) {
  const { flash, csrf_token: csrfToken } = usePage<SharedProps>().props
  const flashRef = useRef<string | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
    if (meta && csrfToken) meta.setAttribute('content', csrfToken)
  }, [csrfToken])

  useEffect(() => {
    const flashKey = JSON.stringify(flash)
    if (flashKey === flashRef.current) return
    flashRef.current = flashKey
    if (flash.success) toast.success(flash.success)
    if (flash.alert) toast.error(flash.alert)
    if (flash.notice) toast.info(flash.notice)
  }, [flash])

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" richColors />

      <div className="flex min-h-screen">
        <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-black/5 bg-muted/40">
          <SidebarBody />
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 items-center justify-between border-b border-black/5 bg-background/80 px-4 backdrop-blur lg:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open navigation">
                  <Menu className="size-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <SheetTitle className="sr-only">Dashboard navigation</SheetTitle>
                <SidebarBody onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <Logo className="text-lg" />
            <div className="w-9" />
          </header>

          <main className="flex-1">
            {children}
          </main>
        </div>
      </div>
    </div>
  )
}
